import React, { useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';

import { validEmail, defaultPersonalMessage, isEmptyString } from 'Shared/helpers';
import { bulkCreateWorkspaces } from 'Shared/actions/workspaces';
import TextField from 'Shared/components/TextField/TextField';
import InviteCollaboratorsInput from 'Dashboard/components/InviteCollaboratorsInput/InviteCollaboratorsInput';
import { WorkspaceType, SelectorValue } from 'Shared/constants/types';
import SimpleAddWorkspaceWrapper from 'Shared/components/modals/SimpleAddWorkspaceModals/SimpleAddWorkspaceWrapper/SimpleAddWorkspaceWrapper';
import { Illustrations } from 'Wpds/components/illustration';
import { useTranslation } from 'Shared/utils/hooks/use-translation';

type Props = {
  onClose: Function;
};

function SimpleAddGroupModal({ onClose }: Props) {
  const dispatch = useDispatch();
  const history = useHistory();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [inviteeEmails, setInviteeEmails] = useState<Array<SelectorValue>>([]);
  const [workspaceName, setWorkspaceName] = useState('');
  const { tr } = useTranslation();

  const handleChangeWorkspaceName = useCallback(event => setWorkspaceName(event.target.value), []);

  const handleInviteesChange = useCallback(value => {
    (window as any).analytics.track('Clicked in Add Workspace Modal', {
      modal: 'add_group',
      button: 'Meeting',
    });
    setInviteeEmails(value);
  }, []);

  const isAllEmailsValid = useCallback(
    () => (inviteeEmails || []).every(({ value }) => validEmail(value)),
    [inviteeEmails]
  );

  const disabledSubmit = isSubmitting || isEmptyString(workspaceName) || !isAllEmailsValid();

  const handleSubmit = async () => {
    setIsSubmitting(true);
    const firstCreatedWorkspace = await dispatch(
      bulkCreateWorkspaces({
        workspacesParams: [
          {
            name: workspaceName,
            isGroup: true,
            inviteeEmails: inviteeEmails.map(({ value }) => value),
            personalMessage: defaultPersonalMessage(workspaceName),
          },
        ],
      })
    );
    if (firstCreatedWorkspace?.path) history.push(firstCreatedWorkspace?.path);
    setIsSubmitting(false);
  };

  return (
    <SimpleAddWorkspaceWrapper
      onClose={onClose}
      workspaceType={WorkspaceType.isGroup}
      title={tr('perform.workspace.simple_add_group.title')}
      description={tr('perform.workspace.simple_add_group.description')}
      disabledSubmit={disabledSubmit}
      isSubmitting={isSubmitting}
      onSubmit={handleSubmit}
      createNameSection={
        <TextField
          name="name"
          type="text"
          label={tr('perform.workspace.simple_add_group.group_name_label')}
          labelMuted={false}
          wrapperClassName="mt-3 pb-3"
          placeholder={tr('perform.workspace.simple_add_group.group_name_placeholder')}
          value={workspaceName}
          onChange={handleChangeWorkspaceName}
        />
      }
      optionToInviteSection={
        <InviteCollaboratorsInput
          value={inviteeEmails}
          label=""
          onChange={handleInviteesChange}
          placeholder={tr('perform.workspace.simple_add_group.invite_placeholder')}
        />
      }
      illustration={Illustrations.two_people_at_table_in_conference_call_purple_bg}
    />
  );
}

export default SimpleAddGroupModal;
